import React from "react";

// Display from 6 AM to 2 AM (21 hours total, leaving out 3 AM - 5 AM)
const DISPLAY_HOURS = [
  6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0, 1, 2
];

const formatTimeLabel = (h) => {
  if (h === 0) return "12 AM";
  if (h === 12) return "12 PM";
  if (h < 12) return `${h} AM`;
  return `${h - 12} PM`;
};

// Labeled checkpoints in header: 6 AM, 9 AM, 12 PM, 3 PM, 6 PM, 9 PM, 12 AM, 2 AM
const isCheckpoint = (h) => [6, 9, 12, 15, 18, 21, 0, 2].includes(h);

const RAMP = [
  { bg: "#ffedd5", text: "#9a3412" }, // Level 1 (orange-100)
  { bg: "#fed7aa", text: "#7c2d12" }, // Level 2 (orange-200)
  { bg: "#fdba74", text: "#431407" }, // Level 3 (orange-300)
  { bg: "#fb923c", text: "#ffffff" }, // Level 4 (orange-400)
  { bg: "#f97316", text: "#ffffff" }, // Level 5 (orange-500)
  { bg: "#dc2626", text: "#ffffff" }, // Level 6 (red-600)
  { bg: "#b91c1c", text: "#ffffff" }, // Level 7 (red-700)
];

const getStep = (n, max) => {
  if (!n || n <= 0) return -1;
  return Math.min(RAMP.length - 1, Math.floor((n / max) * RAMP.length));
};

export default function HourHeatmap({ data }) {
  if (!data) {
    return (
      <div className="ins-card" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div className="ins-card-header">
          <div>
            <h3 className="ins-card-title">Busiest hours</h3>
            <p className="ins-card-subtitle">Orders by hour of day and weekday (IST)</p>
          </div>
        </div>
        <div className="ins-card-body" style={{ minHeight: "260px", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
          Loading hourly analytics…
        </div>
      </div>
    );
  }

  const allOrders = (data.orders || []).flat();
  const max = Math.max(1, ...allOrders);

  // Compute peak hour and day across active operating window
  let peakDay = "";
  let peakHour = 19;
  let peakCount = 0;

  if (data.weekdays && data.orders) {
    data.weekdays.forEach((day, d) => {
      DISPLAY_HOURS.forEach((h) => {
        const cnt = (data.orders[d] && data.orders[d][h]) || 0;
        if (cnt > peakCount) {
          peakCount = cnt;
          peakDay = day;
          peakHour = h;
        }
      });
    });
  }

  const peakBadgeText = peakCount > 0
    ? `Peak: ${peakDay} ${formatTimeLabel(peakHour)}–${formatTimeLabel((peakHour + 2) % 24)}`
    : "Peak: Evening rush";

  return (
    <div className="ins-card" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div className="ins-card-header">
        <div>
          <h3 className="ins-card-title">Busiest hours</h3>
          <p className="ins-card-subtitle">Orders from 6 AM to 2 AM (Indian Standard Time)</p>
        </div>
        <span
          style={{
            padding: "0.2rem 0.6rem",
            fontSize: "0.6875rem",
            fontWeight: 700,
            backgroundColor: "#fff7ed",
            color: "#c2410c",
            border: "1px solid #fed7aa",
            borderRadius: "0.375rem",
          }}
        >
          {peakBadgeText}
        </span>
      </div>

      <div className="ins-card-body">
        <div className="ins-table-wrap custom-scroll" style={{ paddingBottom: "0.5rem" }}>
          <table className="ins-heat-table" style={{ minWidth: "480px" }}>
            <thead>
              <tr>
                <th className="ins-heat-day-label" style={{ border: "none", background: "transparent" }}>Day</th>
                {DISPLAY_HOURS.map((hour) => (
                  <th
                    key={hour}
                    className="ins-heat-header-cell"
                    style={{
                      border: "none",
                      background: "transparent",
                      boxShadow: "none",
                      padding: "0 1px 6px",
                    }}
                  >
                    {isCheckpoint(hour) ? formatTimeLabel(hour) : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.weekdays.map((day, d) => (
                <tr key={day}>
                  <td className="ins-heat-day-label">{day}</td>
                  {DISPLAY_HOURS.map((hour) => {
                    const n = (data.orders[d] && data.orders[d][hour]) || 0;
                    const stepIdx = getStep(n, max);
                    const style =
                      stepIdx < 0
                        ? { backgroundColor: "#f1f5f9", color: "#94a3b8" }
                        : {
                            backgroundColor: RAMP[stepIdx].bg,
                            color: RAMP[stepIdx].text,
                            boxShadow: stepIdx >= 3 ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                          };

                    return (
                      <td
                        key={hour}
                        className="ins-heat-cell"
                        style={style}
                        title={`${day} ${formatTimeLabel(hour)} to ${formatTimeLabel((hour + 1) % 24)} — ${n} orders`}
                      >
                        {n > 0 ? n : ""}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend scale */}
        <div className="ins-heat-legend">
          <span>Fewer orders</span>
          <div className="ins-heat-swatches">
            <span className="ins-heat-swatch" style={{ backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0" }} />
            {RAMP.map((r, i) => (
              <span key={i} className="ins-heat-swatch" style={{ backgroundColor: r.bg }} />
            ))}
          </div>
          <span>More orders</span>
        </div>
      </div>
    </div>
  );
}
